import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  Send,
  CheckCircle,
  XCircle,
  Copy,
  Image,
  FileText,
  MessageCircle,
  File,
  Upload,
  Link,
  Code,
} from "lucide-react";
import AuthFields from "./TokenInstanceFields";
import { sendMessage } from "../../../apis/whatsapp";
import { toast } from "react-toastify";
import { uploadDocumentFile } from "../../../apis/documents";

const API_URL = `${process.env.REACT_APP_API_URL}/api/whatsapp/send-message`;

// Message type constants
const MESSAGE_TYPES = {
  TEXT: "text",
  IMAGE: "image",
  DOCUMENT: "document",
};

// Media input types
const MEDIA_INPUT_TYPES = {
  URL: "url",
  BASE64: "base64",
  UPLOAD: "upload",
};

const LANGUAGE_SNIPPETS = ({
  phoneNumber,
  message,
  token,
  instanceId,
  messageType,
  mediaUrl,
  caption,
}) => {
  const jsonBody = {
    instanceId: instanceId || "<instanceId>",
    phoneNumber: phoneNumber || "<phoneNumber>",
    message: messageType === MESSAGE_TYPES.TEXT ? message || "<message>" : null,
    message_type: messageType || "text",
    ...(messageType !== "text" && { media_url: mediaUrl || "<media_url>" }),
    ...(caption && { caption: caption }),
  };
  const jsonStr = JSON.stringify(jsonBody, null, 2);

  return {
    curl: `curl -X POST ${API_URL} \\
-H "Content-Type: application/json" \\
-H "Authorization: Bearer ${token || "<token>"}" \\
-d '${jsonStr}'`,

    node: `const fetch = require('node-fetch');

const response = await fetch("${API_URL}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${token || "<token>"}"
  },
  body: JSON.stringify(${JSON.stringify(jsonBody, null, 2)})
});

const result = await response.json();
console.log(result);`,

    python: `import requests
import json

headers = {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${token || "<token>"}"
}

data = ${JSON.stringify(jsonBody, null, 2)}

response = requests.post("${API_URL}", json=data, headers=headers)
result = response.json()
print(json.dumps(result, indent=2))`,

    php: `<?php
$data = ${JSON.stringify(jsonBody, null, 2)};

$options = array(
  'http' => array(
    'header'  => "Content-type: application/json\\r\\n" .
                 "Authorization: Bearer ${token || "<token>"}\\r\\n",
    'method'  => 'POST',
    'content' => json_encode($data),
  ),
);
$context = stream_context_create($options);
$result = file_get_contents('${API_URL}', false, $context);
echo $result;
?>`,

    javascript: `fetch("${API_URL}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${token || "<token>"}"
  },
  body: JSON.stringify(${JSON.stringify(jsonBody, null, 2)})
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`,
  };
};

const LANGUAGES = [
  { key: "curl", label: "cURL" },
  { key: "node", label: "Node.js" },
  { key: "python", label: "Python" },
  { key: "php", label: "PHP" },
  { key: "javascript", label: "JavaScript" },
];

const MessagesContent = () => {
  // Auth fields
  const [inputToken, setInputToken] = useState("");
  const [inputInstanceId, setInputInstanceId] = useState("");
  const [token, setToken] = useState("");
  const [instanceId, setInstanceId] = useState("");
  const [editMode, setEditMode] = useState(true);

  // Message fields
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(MESSAGE_TYPES.TEXT);
  const [mediaUrl, setMediaUrl] = useState("");
  const [caption, setCaption] = useState("");

  // Media input type and file handling
  const [mediaInputType, setMediaInputType] = useState(MEDIA_INPUT_TYPES.URL);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [converting, setConverting] = useState(false);
  const fileInputRef = useRef(null);

  // UI state
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [activeLang, setActiveLang] = useState("curl");
  const [copied, setCopied] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const snippetRef = useRef(null);

  const handleSave = () => {
    setToken(inputToken);
    setInstanceId(inputInstanceId);
    setEditMode(false);
  };

  const handleReset = () => {
    setInputToken("");
    setInputInstanceId("");
    setToken("");
    setInstanceId("");
    setEditMode(true);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isImage = messageType === MESSAGE_TYPES.IMAGE;
    const allowedImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    const allowedDocTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];

    if (isImage && !allowedImageTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG, GIF, WebP)");
      return;
    }

    if (
      !isImage &&
      ![...allowedDocTypes, ...allowedImageTypes].includes(file.type)
    ) {
      toast.error("Please select a valid document file (PDF, DOC, DOCX, TXT)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    setConverting(true);
    try {
      const response = await uploadDocumentFile(
        localStorage.getItem("userAccessToken"),
        file
      );
      if (response.data && response.data.success) {
        setMediaUrl(response.data.data.doc_url);
        setUploadedFile(file);
        toast.success("File uploaded successfully!");
      } else {
        toast.error(response.data.error || "File upload failed");
        setMediaUrl("");
        setUploadedFile(null);
      }
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Failed to upload file to server");
      setMediaUrl("");
      setUploadedFile(null);
    } finally {
      setConverting(false);
    }
  };

  const clearUploadedFile = () => {
    setUploadedFile(null);
    setMediaUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = async () => {
    if (isSendDisabled()) return;

    if (mediaInputType === MEDIA_INPUT_TYPES.BASE64) {
      const base64String = mediaUrl.includes(",")
        ? mediaUrl.split(",")[1]
        : mediaUrl;
      const base64Length = base64String.length;
      const sizeInBytes = (base64Length * 3) / 4;
      if (sizeInBytes > 2 * 1024 * 1024) {
        toast.error("Base64 string size must be less than 2MB");
        return;
      }
    }

    setSending(true);
    setStatus(null);
    setApiResponse(null);

    const payload = {
      instanceId,
      phoneNumber,
      message: messageType === MESSAGE_TYPES.TEXT ? message : null,
      message_type: messageType,
      ...(messageType !== MESSAGE_TYPES.TEXT && { media_url: mediaUrl }),
      ...(caption && { caption }),
    };

    try {
      const { data } = await sendMessage(token, payload);
      setApiResponse(data);
      if (data.success) {
        setStatus("success");
        toast.success(data.message || "Message sent successfully!");
        setPhoneNumber("");
        setMessage("");
        setMediaUrl("");
        setCaption("");
        setMessageType(MESSAGE_TYPES.TEXT);
        setMediaInputType(MEDIA_INPUT_TYPES.URL);
        clearUploadedFile();
      } else {
        setStatus("error");
        toast.error(data.error || data.message || "Failed to send message");
      }
    } catch (error) {
      setStatus("error");
      if (error.response?.data) {
        setApiResponse(error.response.data);
        toast.error(
          error.response.data.error ||
            error.response.data.message ||
            "API error occurred"
        );
      } else {
        setApiResponse({
          success: false,
          error: error.message || "Network error occurred",
        });
        toast.error("Network error occurred");
      }
    } finally {
      setSending(false);
    }
  };

  const handleCopy = () => {
    const snippet = LANGUAGE_SNIPPETS({
      phoneNumber,
      message,
      token,
      instanceId,
      messageType,
      mediaUrl,
      caption,
    })[activeLang];

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(snippet).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = snippet;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (err) {
        console.error("Fallback: Oops, unable to copy", err);
      }
      document.body.removeChild(textArea);
    }
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case MESSAGE_TYPES.IMAGE:
        return <Image size={16} />;
      case MESSAGE_TYPES.DOCUMENT:
        return <FileText size={16} />;
      default:
        return <MessageCircle size={16} />;
    }
  };

  const getMediaInputIcon = (type) => {
    switch (type) {
      case MEDIA_INPUT_TYPES.URL:
        return <Link size={14} />;
      case MEDIA_INPUT_TYPES.BASE64:
        return <Code size={14} />;
      case MEDIA_INPUT_TYPES.UPLOAD:
        return <Upload size={14} />;
      default:
        return <Link size={14} />;
    }
  };

  const isSendDisabled = () => {
    if (sending || !token.trim() || !instanceId.trim() || !phoneNumber.trim())
      return true;
    if (messageType === MESSAGE_TYPES.TEXT && !message.trim()) return true;
    if (messageType !== MESSAGE_TYPES.TEXT && !mediaUrl.trim()) return true;
    return false;
  };

  return (
    <motion.div
      className="flex flex-col gap-6 p-6 bg-white rounded-xl shadow-lg border border-gray-200 text-gray-900"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, type: "spring" }}
    >
      <div className="w-full">
        <AuthFields
          inputInstanceId={inputInstanceId}
          setInputInstanceId={setInputInstanceId}
          inputToken={inputToken}
          setInputToken={setInputToken}
          instanceId={instanceId}
          token={token}
          editMode={editMode}
          setEditMode={setEditMode}
          onSave={handleSave}
          onReset={handleReset}
        />

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left - Form */}
          <div className="flex-1">
            <h3 className="mb-4 font-semibold text-lg flex items-center gap-2 text-gray-900">
              <Send size={20} className="text-[#01CC54]" />
              Send WhatsApp Message
            </h3>

            {/* Phone Number */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1 text-gray-600">
                Whatsapp Id
              </label>
              <input
                className="w-full rounded px-3 py-2 border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54]"
                placeholder="e.g 919898947350 or 919898947350@c.us or 37639263748393@g.us"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={editMode}
              />
              <p className="mt-1 text-xs text-gray-500">
                Valid formats: <code>919898947350</code>,{" "}
                <code>919898947350@c.us</code>, <code>37639263748393@g.us</code>
              </p>
            </div>

            {/* Message Type */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1 text-gray-600">
                Message Type
              </label>
              <div className="flex gap-2">
                {Object.values(MESSAGE_TYPES).map((type) => (
                  <button
                    key={type}
                    className={`px-3 py-1 rounded border flex items-center gap-1 text-sm ${
                      messageType === type
                        ? "bg-[#048AAD] text-white border-[#048AAD]"
                        : "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200"
                    }`}
                    onClick={() => {
                      setMessageType(type);
                      if (type === MESSAGE_TYPES.TEXT) {
                        setMediaInputType(MEDIA_INPUT_TYPES.URL);
                        setMediaUrl("");
                        clearUploadedFile();
                      }
                    }}
                    disabled={editMode}
                    type="button"
                  >
                    {getMessageTypeIcon(type)}
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Message (text) */}
            {messageType === MESSAGE_TYPES.TEXT && (
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1 text-gray-600">
                  Message
                </label>
                <textarea
                  className="w-full rounded px-3 py-2 border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54]"
                  placeholder="Type your message here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={editMode}
                  rows={3}
                />
              </div>
            )}

            {/* Media Input Options */}
            {messageType !== MESSAGE_TYPES.TEXT && (
              <>
                <div className="mb-3">
                  <label className="block text-sm font-medium mb-1 text-gray-600">
                    {messageType === MESSAGE_TYPES.IMAGE ? "Image" : "Document"}{" "}
                    Input Method
                  </label>
                  <div className="flex gap-2">
                    {Object.values(MEDIA_INPUT_TYPES).map((type) => (
                      <button
                        key={type}
                        className={`px-3 py-1 rounded border flex items-center gap-1 text-sm ${
                          mediaInputType === type
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200"
                        }`}
                        onClick={() => {
                          setMediaInputType(type);
                          if (type !== MEDIA_INPUT_TYPES.UPLOAD)
                            clearUploadedFile();
                          setMediaUrl("");
                        }}
                        disabled={editMode}
                        type="button"
                      >
                        {getMediaInputIcon(type)}
                        {type === MEDIA_INPUT_TYPES.URL && "URL"}
                        {type === MEDIA_INPUT_TYPES.BASE64 && "Base64"}
                        {type === MEDIA_INPUT_TYPES.UPLOAD && "Upload"}
                      </button>
                    ))}
                  </div>
                </div>

                {mediaInputType === MEDIA_INPUT_TYPES.URL && (
                  <div className="mb-3">
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      {messageType === MESSAGE_TYPES.IMAGE
                        ? "Image URL"
                        : "Document URL"}
                    </label>
                    <input
                      className="w-full rounded px-3 py-2 border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54]"
                      placeholder={
                        messageType === MESSAGE_TYPES.IMAGE
                          ? "https://example.com/image.jpg"
                          : "https://example.com/document.pdf"
                      }
                      value={mediaUrl}
                      onChange={(e) => setMediaUrl(e.target.value)}
                      disabled={editMode}
                    />
                  </div>
                )}

                {mediaInputType === MEDIA_INPUT_TYPES.BASE64 && (
                  <div className="mb-3">
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      Base64 String
                    </label>
                    <textarea
                      className="w-full rounded px-3 py-2 border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54]"
                      placeholder="data:image/jpeg;base64,/9j/4AAQSk... or just the base64 string"
                      value={mediaUrl}
                      onChange={(e) => setMediaUrl(e.target.value)}
                      disabled={editMode}
                      rows={4}
                    />
                  </div>
                )}

                {mediaInputType === MEDIA_INPUT_TYPES.UPLOAD && (
                  <div className="mb-3">
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      Upload{" "}
                      {messageType === MESSAGE_TYPES.IMAGE
                        ? "Image"
                        : "Document"}
                    </label>
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 bg-gray-50">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={
                          messageType === MESSAGE_TYPES.IMAGE
                            ? "image/*"
                            : ".pdf,.doc,.docx,.txt"
                        }
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={editMode || converting}
                      />

                      {!uploadedFile ? (
                        <div className="text-center">
                          <Upload className="mx-auto h-12 w-12 text-gray-500" />
                          <div className="mt-2">
                            <button
                              type="button"
                              className="text-sm font-medium text-[#01CC54] hover:text-[#01CC54]/80 disabled:opacity-50"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={editMode || converting}
                            >
                              {converting ? "Converting..." : "Choose file"}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {messageType === MESSAGE_TYPES.IMAGE
                              ? "PNG, JPG, GIF, WebP up to 5MB"
                              : "PDF, DOC, DOCX, TXT up to 5MB"}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {messageType === MESSAGE_TYPES.IMAGE ? (
                              <Image className="h-5 w-5 text-[#01CC54]" />
                            ) : (
                              <File className="h-5 w-5 text-[#01CC54]" />
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {uploadedFile.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(uploadedFile.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="text-red-600 hover:text-red-700 text-sm"
                            onClick={clearUploadedFile}
                            disabled={editMode}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="block text-sm font-medium mb-1 text-gray-600">
                    Caption (Optional)
                  </label>
                  <input
                    className="w-full rounded px-3 py-2 border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54]"
                    placeholder="Enter caption for media..."
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    disabled={editMode}
                  />
                </div>
              </>
            )}

            {/* Send Button */}
            <motion.button
              className={`w-full flex items-center justify-center gap-2 rounded py-3 font-medium ${
                isSendDisabled() || converting
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-[#048AAD] hover:bg-[#048AAD]/90 text-white"
              }`}
              onClick={handleSend}
              disabled={isSendDisabled() || converting}
              whileTap={{ scale: isSendDisabled() || converting ? 1 : 0.98 }}
              whileHover={isSendDisabled() || converting ? {} : { scale: 1.01 }}
              type="button"
            >
              <Send size={20} />
              {converting
                ? "Converting..."
                : sending
                ? "Sending..."
                : "Send Message"}
            </motion.button>

            {/* Status & Response */}
            <div className="mt-4">
              {status === "success" && (
                <motion.div
                  className="flex items-center text-[#01CC54] gap-2 mb-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <CheckCircle size={18} />
                  <span>Message sent successfully!</span>
                </motion.div>
              )}
              {status === "error" && (
                <motion.div
                  className="flex items-center text-red-600 gap-2 mb-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <XCircle size={18} />
                  <span>Failed to send message</span>
                </motion.div>
              )}

              {apiResponse && (
                <motion.div
                  className="bg-gray-50 p-3 rounded border border-gray-200 text-sm"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h4 className="font-medium mb-2 text-gray-900">
                    API Response:
                  </h4>
                  <pre className="whitespace-pre-wrap text-xs overflow-auto max-h-32 text-gray-700">
                    {JSON.stringify(apiResponse, null, 2)}
                  </pre>
                </motion.div>
              )}
            </div>
          </div>

          {/* Right - API Documentation */}
          <motion.div
            className="flex-1 lg:flex-2 bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-inner flex flex-col"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="mb-3 font-semibold flex items-center gap-2 text-gray-900">
              <File size={18} className="text-[#01CC54]" />
              API Documentation
            </h4>

            {/* Endpoint */}
            <motion.div
              className="mb-4 p-3 bg-gray-50 rounded border border-gray-200 shadow-sm"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-[#01CC54]/10 text-[#01CC54] px-2 py-1 rounded text-xs font-mono border border-[#01CC54]/30">
                  POST
                </span>
                <code className="text-sm font-mono break-all text-gray-700">
                  {API_URL}
                </code>
              </div>
            </motion.div>

            {/* Request Body Documentation */}
            <div className="mb-4">
              <h5 className="font-medium mb-2 text-gray-900">
                Request Body Parameters:
              </h5>
              <div className="text-sm space-y-1 bg-gray-50 p-3 rounded border border-gray-200 text-gray-700">
                <div>
                  <code className="text-blue-600">instanceId</code>{" "}
                  <span className="text-red-600">*</span> - WhatsApp instance ID
                </div>
                <div>
                  <code className="text-blue-600">phoneNumber</code>{" "}
                  <span className="text-red-600">*</span> - Recipient phone
                  number
                </div>
                <div>
                  <code className="text-blue-600">message</code>{" "}
                  {messageType === MESSAGE_TYPES.TEXT ? (
                    <span className="text-red-600">*</span>
                  ) : (
                    <span className="text-gray-500">(null for media)</span>
                  )}{" "}
                  - Message content
                </div>
                <div>
                  <code className="text-blue-600">message_type</code> - "text",
                  "image", or "document" (default: "text")
                </div>
                <div>
                  <code className="text-blue-600">media_url</code>{" "}
                  {messageType !== MESSAGE_TYPES.TEXT && (
                    <span className="text-red-600">*</span>
                  )}{" "}
                  - URL or base64 for image/document
                </div>
                <div>
                  <code className="text-blue-600">caption</code> - Optional
                  caption for media
                </div>
              </div>
            </div>

            {/* Language selector */}
            <div className="mb-3 flex flex-wrap gap-2">
              {LANGUAGES.map(({ key, label }) => (
                <button
                  key={key}
                  className={`px-2 py-1 rounded text-sm font-medium border ${
                    activeLang === key
                      ? "bg-[#048AAD] text-white border-[#048AAD]"
                      : "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200"
                  }`}
                  onClick={() => setActiveLang(key)}
                  type="button"
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Code snippet */}
            <div className="flex-1 relative">
              <div
                ref={snippetRef}
                className="bg-gray-50 rounded p-3 relative h-full min-h-[200px] overflow-auto border border-gray-200"
              >
                <pre className="text-gray-900 text-sm whitespace-pre-wrap">
                  {
                    LANGUAGE_SNIPPETS({
                      phoneNumber,
                      message,
                      token,
                      instanceId,
                      messageType,
                      mediaUrl,
                      caption,
                    })[activeLang]
                  }
                </pre>
                <button
                  onClick={handleCopy}
                  className="absolute top-2 right-2 bg-gray-100 hover:bg-gray-200 p-2 rounded text-gray-700 transition-colors border border-gray-200"
                  title="Copy to clipboard"
                  type="button"
                >
                  <Copy size={16} />
                </button>
                {copied && (
                  <span className="absolute top-2 right-12 text-xs bg-[#048AAD] text-white px-2 py-1 rounded">
                    Copied!
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default MessagesContent;
